import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Alert,
  AlertTitle,
  CircularProgress,
  Divider,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  Calculate as CalculateIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { checkCredits, getUserTier, formatCredits, getWarningLevel } from '../../services/usageService';

/**
 * CreditEstimator - Shows credit estimation and warnings for research jobs
 * 
 * This component displays:
 * - Estimated credits needed
 * - Credit availability check
 * - Warnings for insufficient credits
 * - Tier limits validation
 */
export default function CreditEstimator({ 
  websiteCount = 0, 
  creditsPerWebsite = 1,
  enhancedWebSearch = false,
  onCreditCheck = null,
  showTierLimits = true,
  autoCheck = true 
}) {
  const [creditCheck, setCreditCheck] = useState(null);
  const [tierInfo, setTierInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Calculate estimated credits
  const estimatedCredits = websiteCount * creditsPerWebsite;

  // Perform credit check when website count or credits per website changes
  useEffect(() => {
    if (autoCheck && estimatedCredits > 0) {
      performCreditCheck();
    }
  }, [websiteCount, creditsPerWebsite, autoCheck, estimatedCredits]);

  const performCreditCheck = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [checkResult, tierResult] = await Promise.all([
        checkCredits(estimatedCredits),
        getUserTier()
      ]);
      
      if (checkResult.success) {
        setCreditCheck(checkResult.data);
        if (onCreditCheck) {
          // Allow partial execution - always report canSubmit as true if user has any credits
          const data = checkResult.data;
          const allowPartialExecution = data.creditsAvailable > 0;
          onCreditCheck({
            ...data,
            hasCredits: data.hasCredits || allowPartialExecution // Allow submission for partial execution
          });
        }
      }
      
      if (tierResult.success) {
        setTierInfo(tierResult.data);
      }
    } catch (err) {
      console.error('Error checking credits:', err);
      setError('Failed to check credit availability');
    } finally {
      setLoading(false);
    }
  };

  // Don't show anything if no websites to estimate
  if (!websiteCount || websiteCount === 0) {
    return null;
  }

  // Loading state
  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2">Checking credit availability...</Typography>
        </Box>
      </Paper>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        <AlertTitle>Credit Check Unavailable</AlertTitle>
        {error}
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2">
            Estimated credits needed: <strong>{formatCredits(websiteCount)}</strong>
          </Typography>
        </Box>
      </Alert>
    );
  }

  // Success state with credit information
  const hasCredits = creditCheck?.hasCredits ?? false;
  const creditsAvailable = creditCheck?.creditsAvailable ?? 0;
  const isUnlimited = creditsAvailable === -1;
  
  // Safely handle website count
  const safeWebsiteCount = Math.max(0, websiteCount || 0);
  
  // Allow partial execution - show warning instead of error for insufficient credits
  // Using Wyspur brand colors: blue for success, orange for warning, pink for error
  const severity = hasCredits ? 'success' : (creditsAvailable > 0 ? 'warning' : 'error');
  const icon = hasCredits ? <CheckCircleIcon /> : (creditsAvailable > 0 ? <WarningIcon /> : <ErrorIcon />);
  
  // Wyspur brand colors
  const wyspurColors = {
    blue: '#0f71fb',    // Success/positive
    orange: '#ffaa01',  // Warning/70% threshold
    pink: '#df3e99'     // Error/critical
  };

  // Check tier limits - fix field mapping
  const maxWebsitesPerJob = tierInfo?.max_websites_per_job ?? -1;
  const exceedsTierLimits = tierInfo && 
    maxWebsitesPerJob !== -1 && 
    safeWebsiteCount > maxWebsitesPerJob;

  return (
    <Box sx={{ mb: 2 }}>
      {/* Credit Estimation */}
      <Alert 
        severity={severity} 
        icon={icon}
        sx={{ 
          mb: exceedsTierLimits ? 1 : 0,
          '& .MuiAlert-icon': {
            color: hasCredits ? wyspurColors.blue : (creditsAvailable > 0 ? wyspurColors.orange : wyspurColors.pink)
          },
          borderColor: hasCredits ? wyspurColors.blue : (creditsAvailable > 0 ? wyspurColors.orange : wyspurColors.pink)
        }}
      >
        <AlertTitle>
          Credit Estimation
          <Tooltip title={`Each website analyzed consumes ${creditsPerWebsite} credit${creditsPerWebsite > 1 ? 's' : ''}${enhancedWebSearch ? ' (includes enhanced web search)' : ''}`}>
            <IconButton size="small" sx={{ ml: 1 }}>
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </AlertTitle>
        
        <Box sx={{ display: 'flex', flex: 1, alignItems: 'center', gap: 2, mb: 1 }}>
          <Typography variant="body2">
            <strong>{formatCredits(estimatedCredits)} credits</strong> needed{enhancedWebSearch ? ` (${safeWebsiteCount} × ${creditsPerWebsite})` : ''} • {' '}
            {isUnlimited ? (
              <span><strong>Unlimited</strong> available</span>
            ) : (
              <span><strong>{formatCredits(creditsAvailable)}</strong> available</span>
            )}
            {' '}({tierInfo?.displayName || tierInfo?.name || 'Unknown'} plan)
          </Typography>
        </Box>

        {/* Status chips */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            icon={<CalculateIcon />}
            label={`${safeWebsiteCount} websites`}
            variant="outlined"
            size="small"
          />
          
          {hasCredits ? (
            <Chip
              icon={<CheckCircleIcon />}
              label="Credits available"
              variant="outlined"
              size="small"
              sx={{ 
                color: wyspurColors.blue,
                borderColor: wyspurColors.blue,
                '& .MuiChip-icon': { color: wyspurColors.blue }
              }}
            />
          ) : creditsAvailable > 0 ? (
            <Chip
              icon={<WarningIcon />}
              label="Partial execution"
              variant="outlined"
              size="small"
              sx={{ 
                color: wyspurColors.orange,
                borderColor: wyspurColors.orange,
                '& .MuiChip-icon': { color: wyspurColors.orange }
              }}
            />
          ) : (
            <Chip
              icon={<ErrorIcon />}
              label="No credits"
              variant="outlined"
              size="small"
              sx={{ 
                color: wyspurColors.pink,
                borderColor: wyspurColors.pink,
                '& .MuiChip-icon': { color: wyspurColors.pink }
              }}
            />
          )}
          
          {!isUnlimited && creditCheck && (
            <Chip
              label={`${formatCredits(Math.max(0, creditsAvailable - safeWebsiteCount))} remaining after`}
              variant="outlined"
              size="small"
              sx={{ 
                color: creditsAvailable - safeWebsiteCount <= 0 ? wyspurColors.orange : 
                       creditsAvailable - safeWebsiteCount < 100 ? wyspurColors.orange : 'inherit',
                borderColor: creditsAvailable - safeWebsiteCount <= 0 ? wyspurColors.orange : 
                            creditsAvailable - safeWebsiteCount < 100 ? wyspurColors.orange : 'inherit'
              }}
            />
          )}
        </Box>

        {/* Insufficient credits message */}
        {!hasCredits && !isUnlimited && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ color: creditsAvailable > 0 ? wyspurColors.orange : wyspurColors.pink }}>
              Insufficient credits: Will process {formatCredits(creditsAvailable)} websites and stop when exhausted.
            </Typography>
          </Box>
        )}
      </Alert>

      {/* TEMPORARILY DISABLED: Plan limits are temporarily disabled in backend 
      {showTierLimits && exceedsTierLimits && (
        <Alert severity="warning" icon={<WarningIcon />}>
          <AlertTitle>Plan Limit Exceeded</AlertTitle>
          <Typography variant="body2">
            Your {tierInfo?.displayName || tierInfo?.name || 'current'} plan allows up to{' '}
            <strong>{formatCredits(maxWebsitesPerJob)} websites per job</strong>, 
            but you've selected {safeWebsiteCount} websites.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Consider reducing the number of websites or upgrading to a higher tier.
          </Typography>
        </Alert>
      )}
      */}
    </Box>
  );
}